// src/appRWAPromo/pages/PredictionEventPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  
  Divider,
  
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";

import PredictionTradingTerminal from "../components/PredictionTradingTerminal";

import { db } from "../../components/firebase";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.94) 0%, rgba(10,13,17,.94) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  borderSoft: "1px solid rgba(51,65,85,.8)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const chipSx = {
  background: "rgba(15,23,42,.85)",
  color: "#e5e7eb",
  border: "1px solid rgba(148,163,184,.45)",
  borderRadius: 999,
  fontSize: 11,
  height: 24,
  "& .MuiChip-label": { px: 1.1, fontWeight: 900 },
};

const fmtNum = (v) => {
  const x = Number(v || 0);
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return String(x);
};



const fmtPerc = (v) => `${v.toFixed(0)}%`;

export default function PredictionEventPage() {
  const { slugAndId } = useParams();
  const navigate = useNavigate();
   const location = useLocation(); 
  const [item, setItem] = useState(null);
  const [heroImg, setHeroImg] = useState(""); // progressive: posterLow -> poster
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  

  const id = useMemo(() => {
    if (!slugAndId) return null;
    const parts = String(slugAndId).split("-");
    return parts[parts.length - 1] || null;
  }, [slugAndId]);


  // ✅ читаем параметры из URL: ?side=yes&stake=100
  const urlSide = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const s = p.get("side");
    return s === "no" ? "no" : "yes";
  }, [location.search]);

  const urlStake = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const v = p.get("stake");
    return v ? String(v) : "100";
  }, [location.search]);


  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const ref = doc(db, "promos", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Event not found");
        if (cancelled) return;

        const data = snap.data() || {};

        const desc = data.description || {};
        const descriptionText =
          typeof desc === "string"
            ? desc
            : [desc.technicalAnalysis, desc.fundamental, desc.news, desc.conclusion]
                .filter(Boolean)
                .join("\n\n");

        setItem({
          id: snap.id,
          type: "prediction",
          ...data,
          description: descriptionText,
        });
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // progressive hero image: posterLow -> poster
  useEffect(() => {
    if (!item) return;

    const low = item.posterLow || item.imageLow || "";
    const hi = item.poster || "";

    // instantly render low (or hi)
    setHeroImg(low || hi || "");

    // then upgrade to hi
    if (hi && hi !== low) {
      const img = new Image();
      img.decoding = "async";
      img.src = hi;
      img.onload = () => setHeroImg(hi);
    }
  }, [item?.id]);

  if (!id) {
    return (
      <Box sx={{ p: 3, color: GLASS.text }}>
        <Typography variant="h6">Invalid event URL</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box sx={{ p: 3, color: GLASS.text }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Event not found
        </Typography>
        <Typography sx={{ color: GLASS.subtext, fontSize: 14 }}>
          {error?.message || "This prediction event does not exist or was removed."}
        </Typography>
      </Box>
    );
  }

  const title = item.name || item.title || item.marketTitle || "Prediction";
  const subtitle = item.handle || item.creatorHandle || "";
  const category = item.category || item.topic || item.market || "Crypto";
  const status = item.status ? String(item.status).toUpperCase() : null;

  // yes / no цены
  const yesPrice = Number(item.yesPrice ?? item.priceYes ?? 0.18);
  const noPrice = Number(item.noPrice ?? item.priceNo ?? Math.max(0, 1 - yesPrice));

  const yesProb = yesPrice * 100;
  const noProb = Math.max(0, 100 - yesProb);

  

  const rulesSummary = `
If the spot price of Ethereum in U.S. dollars is above 4953.73 by Dec 31, 2025 at 11:59 PM,
then the market resolves to Yes. Outcome verified from CF Benchmarks.

Scope: These rules apply to this contract.
Underlying: ETHUSD spot price according to the CF Ethereum Real-Time Index (ETHUSD_RTI).
The expiration value is calculated as a trimmed mean excluding the top 20% and bottom 20% of
minute-by-minute values between issuance and the expiration time on the expiration date.

Payout criterion: The contract resolves to Yes if the expiration value is above the strike price
by the expiration time; otherwise it resolves to No. If no data is available on the expiration
date at the expiration time, the market resolves to No.

Settlement: Each winning contract settles at $1.00 on the settlement date (no later than the day
after the expiration date).
  `.trim();

  const demoMessages = [
    { id: 1, author: "QuantAlpha", ts: "2m ago", text: "Accumulating BTC on every dip, halving + ETF flows make 200k plausible." },
    { id: 2, author: "MacroBear", ts: "9m ago", text: "Need another 4–5x from here, feels stretched on this horizon." },
    { id: 3, author: "OnChainSignals", ts: "21m ago", text: "Long-term holder supply still supports multi-year uptrend." },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#020617",
        color: GLASS.text,
        py: 3,
        px: { xs: 1.5, sm: 2.5, md: 4 },
      }}
    >
      {/* HEADER */}
      <Box sx={{ maxWidth: 1200, mx: "auto", mb: 2.5 }}>
        <Button
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/market");
          }}
          variant="text"
          size="small"
          sx={{
            mb: 1,
            color: GLASS.subtext,
            textTransform: "none",
            fontSize: 13,
            "&:hover": { color: "#e5e7eb" },
          }}
        >
          ← Back to Market
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Left */}
          <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Chip label="PREDICTION EVENT" sx={chipSx} />
              {status ? <Chip label={status} sx={chipSx} /> : null}
              {category ? <Chip label={category} sx={chipSx} /> : null}
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-.03em", mb: 0.5 }}>
              {title}
            </Typography>

            {subtitle && (
              <Typography sx={{ color: GLASS.subtext, fontSize: 14, mb: 0.75 }}>
                {subtitle}
              </Typography>
            )}

            <Typography sx={{ color: GLASS.subtext, fontSize: 13, mb: 0.5 }}>
              Views: <b>{fmtNum(item.views)}</b>
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", fontSize: 12, color: GLASS.subtext }}>
              <Typography>
                Yes implied: <b>{fmtPerc(yesProb)}</b>
              </Typography>
              <Typography>
                No implied: <b>{fmtPerc(noProb)}</b>
              </Typography>
              {item.asset && (
                <Typography>
                  Underlying: <b>{item.asset}</b>
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right hero image: posterLow -> poster */}
          {heroImg ? (
            <Box
              component="img"
              src={heroImg}
              alt={title}
              sx={{
                flexShrink: 0,
                width: 240,
                height: 140,
                borderRadius: 2,
                border: "1px solid rgba(148,163,184,.55)",
                objectFit: "cover",
                boxShadow: "0 10px 30px rgba(15,23,42,.85)",
              }}
            />
          ) : null}
        </Box>
      </Box>

      {/* MAIN GRID */}
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Grid container spacing={2.5}>
          {/* LEFT */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: GLASS.bg,
                border: GLASS.border,
                boxShadow: "0 18px 40px rgba(15,23,42,.9)",
                mb: 2,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 2.1 }}>
                <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <TimelineIcon sx={{ color: "#38bdf8" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: GLASS.text }}>
                    Market overview
                  </Typography>

                  <Chip
                    size="small"
                    label="ALL"
                    sx={{
                      ml: "auto",
                      height: 22,
                      fontSize: 11,
                      borderRadius: 999,
                      color: GLASS.text,
                      border: GLASS.borderSoft,
                      bgcolor: "rgba(15,23,42,.95)",
                    }}
                  />
                  <Chip size="small" label="1M" sx={{ height: 22, fontSize: 11, borderRadius: 999, color: GLASS.subtext, border: GLASS.borderSoft, bgcolor: "transparent" }} />
                  <Chip size="small" label="1W" sx={{ height: 22, fontSize: 11, borderRadius: 999, color: GLASS.subtext, border: GLASS.borderSoft, bgcolor: "transparent" }} />
                </Box>

                {/* Placeholder chart */}
                <Box
                  sx={{
                    height: 220,
                    borderRadius: 2,
                    position: "relative",
                    background:
                      "radial-gradient(900px circle at 0% 0%, rgba(56,189,248,.28), transparent 55%), linear-gradient(135deg, rgba(15,23,42,1), rgba(15,23,42,.75))",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        "linear-gradient(to right, rgba(30,64,175,.35) 1px, transparent 1px), linear-gradient(to top, rgba(30,64,175,.35) 1px, transparent 1px)",
                      backgroundSize: "46px 38px",
                      opacity: 0.4,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg width='400' height='170' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 120 Q 40 40 80 70 T 160 60 T 240 90 T 320 45 T 400 70' fill='none' stroke='rgba(96,165,250,.95)' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E\")",
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.9,
                    }}
                  />
                </Box>

                {/* Crowd positioning */}
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: GLASS.subtext, mb: 1 }}>
                    Crowd positioning by strike
                  </Typography>

                  {[84_000, 86_000, 88_000, 90_000].map((strike, idx) => {
                    const yesShare = [0.82, 0.74, 0.61, 0.45][idx];
                    return (
                      <Box
                        key={strike}
                        sx={{
                          mb: 0.75,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: 11,
                          color: GLASS.subtext,
                        }}
                      >
                        <Box sx={{ width: 74, fontVariantNumeric: "tabular-nums" }}>
                          ${strike.toLocaleString("en-US")}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            height: 18,
                            borderRadius: 999,
                            background: "rgba(15,23,42,1)",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: `${yesShare * 100}%`,
                              background: "linear-gradient(90deg, rgba(34,197,94,.9), rgba(59,130,246,.9))",
                            }}
                          />
                        </Box>
                        <Box sx={{ width: 40, textAlign: "right" }}>{fmtPerc(yesShare * 100)}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>

            </Card>

            {/* Analysis */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: GLASS.bg,
                border: GLASS.border,
                boxShadow: "0 18px 40px rgba(15,23,42,.9)",
              }}
            >
              <CardContent sx={{ p: 2.1 }}>
                <Typography sx={{ fontWeight: 800, mb: 1, fontSize: 14 }}>Analysis</Typography>
                <Typography sx={{ color: GLASS.subtext, fontSize: 13, whiteSpace: "pre-line" }}>
                  {item.description || "No analysis yet."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: GLASS.bg,
                border: GLASS.border,
                boxShadow: "0 18px 40px rgba(15,23,42,.9)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ p: 2.1 }}>
  <PredictionTradingTerminal
    key={`${item.id}_${urlSide}_${urlStake}`}   // ✅ чтобы при смене URL терминал переинициализировался
    market={item}
    marketId={item.id}
    initialSide={urlSide}
    initialStake={urlStake}
    onTradeSuccess={(res) => {
      console.log("trade success", res);
      // позже: toast + refresh positions/ledger
    }}
  />
</CardContent>

            </Card>
          </Grid>
        </Grid>

        {/* Rules + Chat */}
        <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
          <Grid item xs={12} md={7}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: GLASS.bg,
                border: GLASS.border,
                boxShadow: "0 18px 40px rgba(15,23,42,.9)",
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2.1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: GLASS.text, mb: 1 }}>
                  Rules summary
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: "rgba(30,64,175,.6)" }} />
                <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: GLASS.subtext, whiteSpace: "pre-line" }}>
                  {rulesSummary}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: GLASS.bg,
                border: GLASS.border,
                boxShadow: "0 18px 40px rgba(15,23,42,.9)",
                display: "flex",
                flexDirection: "column",
                height: 320,
              }}
            >
              <CardContent
                sx={{
                  p: 2.1,
                  pb: 1.4,
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: GLASS.text, mb: 1 }}>
                  Event chat
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: "rgba(30,64,175,.6)" }} />

                <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.1, pr: 0.5 }}>
                  {demoMessages.map((m) => (
                    <Box key={m.id} sx={{ fontSize: 12 }}>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, color: GLASS.text }}>{m.author}</Typography>
                        <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>{m.ts}</Typography>
                      </Box>
                      <Typography sx={{ color: GLASS.subtext }}>{m.text}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 1.4 }}>
                  <TextField
                    placeholder="Write a message about this event…"
                    fullWidth
                    size="small"
                    InputProps={{
                      sx: {
                        fontSize: 12,
                        bgcolor: "rgba(15,23,42,.96)",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "rgba(148,163,184,.7)" },
                        "&:hover fieldset": { borderColor: GLASS.borderSoft },
                        "&.Mui-focused fieldset": { borderColor: GLASS.borderSoft },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
